import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';
import {
  fetchTermsAndConditions,
  type TermsAndConditionsApi,
} from '@/api/cms/termsAndConditions';
import { formatDate } from '@/helpers/common';
import { Country } from '@/helpers/conf';
import { Locale } from '@/helpers/languages';
import { useCurrentCountry } from '@/hooks/useCurrentCountry';

import { getPoliciesByCountryCode } from '@/modules/policies/helpers';
import { useGetCommonStore } from '@/store/commonStore';

import { StaticModal, type StaticModalProps } from '../../StaticModal';

export const TermsAndConditionsModalContainer: FC<StaticModalProps> = ({
  onClose,
  isPortal,
}) => {
  const {
    i18n: { language },
  } = useTranslation('common');

  const isAustralia = useCurrentCountry(Country.Australia);

  const { data, isLoading } = useSWRImmutable<TermsAndConditionsApi>(
    ['termsAndConditions', language],
    () =>
      fetchTermsAndConditions(isAustralia ? Locale.ENGLISH_AUSTRALIA : language)
  );

  if (!data) return null;

  return (
    <TermsAndConditionsModal
      data={data}
      isLoading={isLoading}
      isPortal={isPortal}
      onClose={onClose}
    />
  );
};

interface TermsAndConditionsModalProps extends StaticModalProps {
  data: TermsAndConditionsApi;
  isLoading: boolean;
}

export const TermsAndConditionsModal: FC<TermsAndConditionsModalProps> = ({
  onClose,
  isPortal,
  data,
  isLoading,
}) => {
  const { t } = useTranslation('common');

  const useCommonStore = useGetCommonStore();
  const countryCode = useCommonStore.use.countryCode() as Country;

  const mainText = getPoliciesByCountryCode(countryCode, data);

  return (
    <StaticModal
      isPortal={isPortal}
      onClose={onClose}
      pending={isLoading}
      title={data.title}
    >
      <div className="date">
        {t('effectiveFrom')}:{' '}
        {formatDate(data.effectiveFrom, t, 'common:month')}
        <br />
        {t('lastUpdated')}: {formatDate(data.lastUpdated, t, 'common:month')}
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: mainText,
        }}
      />
    </StaticModal>
  );
};
